import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import selectors from 'state/selectors';
import service from 'services';
import utils from 'utils';
import TextArea from 'components/Input/Textarea';
import Table, { TableColumn } from 'components/Table';
import Modal from 'components/Modal';
import actions from 'state/actions';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import Icon from 'components/Input/Icon';

const Title = styled.p`
  margin: 20px 0 10px 0;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const ResultTableContainer = styled.div`
  padding: 15px;
  max-height: 400px;
  width: 700px;
  overflow: auto;
`;

const CloseLitigation = () => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();
  const documentsIDS = useSelector(selectors.documents.selectAllActiveDocumentsID);
  const [note, setNote] = useState<string>('');
  const [showResults, setShowResults] = useState(false);
  const [resultRows, setResultRows] = useState<
    {
      protocol: number;
      success: boolean;
      message: string;
    }[]
  >([]);

  const resultColumns: TableColumn[] = [
    {
      Header: t('status'),
      accessor: 'success',
      width: 70,
      Cell: ({ value }: { value: boolean }) => (
        <span style={{ display: 'flex', alignContent: 'center', justifyContent: 'center' }}>
          <Icon
            icon={value ? 'fa-check' : 'exclamation-circle-lucy'}
            style={{
              color: value ? 'green' : 'red',
            }}
            size="sm"
          />
        </span>
      ),
    },
    {
      Header: t('protocol_number'),
      accessor: 'protocol',
      width: 140,
    },
    {
      Header: t('info'),
      accessor: 'message',
      width: 430,
      Cell: ({ value }: { value: string }) => (
        <div
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {utils.app.textTranslated(value)}
        </div>
      ),
    },
  ];

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const handleConfirm = async () => {
    try {
      if (documentsIDS.length > 0) {
        const request = {
          protocols: documentsIDS,
          note,
        };
        const { data } = await service.closeCloseLitigation(request);
        // Backend always returns response array
        setShowResults(true);
        setResultRows(data);
      }
    } catch (err) {
      console.error(err);
      utils.app.notify('fail', t('error_closing_litigation'));
    }
  };

  const handleResultsClose = () => {
    const successfulProtocols = resultRows.filter((item) => item.success).map((item) => item.protocol);

    if (successfulProtocols.length > 0) {
      dispatch(actions.documents.removeRows(successfulProtocols));
    }

    dispatch(actions.documents.setActiveDocumentId([]));
    dispatch(actions.modal.closeModal());
    setShowResults(false);
  };

  if (showResults) {
    return (
      <>
        <Modal.Header title={t('close_litigation_results_title')} subtitle={t('close_litigation_results_subtitle')} />
        <ResultTableContainer>
          <Table
            id="close-litigation-table"
            columns={resultColumns}
            rows={resultRows}
            hasSort
            hasPagination
            hasExport={false}
          />
        </ResultTableContainer>
        <Modal.Footer withConfirmButton={false} closeButtonText={t('close')} cancelAction={handleResultsClose} />
      </>
    );
  }

  return (
    <>
      <Modal.Header
        title={t('close_litigation_title')}
        subtitle={
          documentsIDS.length > 1 ? t('close_litigation_multiple_documents_info') : t('close_litigation_subtitle')
        }
      />
      <Modal.Content>
        <Section>
          <Title>{t('note')}</Title>
          <TextArea name="Nota" placeholder={t('note here')} onChange={onNoteChange} minHeight="62px" />
        </Section>
      </Modal.Content>
      <Modal.Footer
        confirmText={t('continue')}
        confirmAction={handleConfirm}
        closeButtonText={t('close')}
        dontCloseOnConfirm
      />
    </>
  );
};

export default CloseLitigation;
